import { useCallback, useMemo, useRef, useState } from 'react';

const DEFAULT_START_VALUE = 60000;
const DEFAULT_INTERVAL = 1000;
const DEFAULT_STEP = 1000;

/**
 * A hook that provides a simple countdown timer
 * @param startValue - The time left on the clock in ms
 * @param startStep - The value of a step in ms
 * @param startInterval - The interval time in ms
 */
export const useCountdown = (
  startValue = DEFAULT_START_VALUE,
  startStep = DEFAULT_STEP,
  startInterval = DEFAULT_INTERVAL
) => {
  const loop = useRef<number>();
  const intervalRef = useRef(startInterval);
  const stepRef = useRef(startStep);
  const valueRef = useRef(startValue);

  const [value, setValue] = useState(startValue);

  const tick = useCallback(() => {
    valueRef.current = valueRef.current - stepRef.current;
    setValue(valueRef.current);
  }, []);

  const start = useCallback(async () => {
    if (loop.current === undefined) {
      loop.current = window.setInterval(() => tick(), intervalRef.current);
    }
  }, [tick]);

  const stop = useCallback(async () => {
    window.clearInterval(loop.current);
    loop.current = undefined;
  }, []);

  const updateInterval = useCallback(
    (newInterval: number) => {
      intervalRef.current = newInterval;
      if (loop.current !== undefined) {
        stop();
        start();
      }
    },
    [start, stop]
  );

  const updateValue = useCallback((newValue: number) => {
    valueRef.current = newValue;
    setValue(newValue);
  }, []);

  return useMemo(() => ({ value, start, stop, updateInterval, updateValue }), [
    start,
    stop,
    updateInterval,
    updateValue,
    value
  ]);
};
